/**
 * Status badge for discovery scans
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ScanStatus } from '@/types/discovery'

interface ScanStatusBadgeProps {
  readonly status: ScanStatus
  readonly className?: string
}

const statusConfig: Record<
  ScanStatus,
  { label: string; badgeClass: string }
> = {
  PENDING: {
    label: 'Queued',
    badgeClass: 'border-muted-foreground/30 text-muted-foreground',
  },
  RUNNING: {
    label: 'Running',
    badgeClass:
      'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
  },
  COMPLETED: {
    label: 'Complete',
    badgeClass:
      'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
  },
  FAILED: {
    label: 'Failed',
    badgeClass: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400',
  },
  CANCELLED: {
    label: 'Cancelled',
    badgeClass:
      'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
  },
}

export function ScanStatusBadge({ status, className }: ScanStatusBadgeProps) {
  const config = statusConfig[status] ?? {
    label: status ?? 'Unknown',
    badgeClass: 'border-muted-foreground/30 text-muted-foreground',
  }

  return (
    <Badge variant="outline" className={cn(config.badgeClass, className)}>
      {config.label}
    </Badge>
  )
}
